/**
 * AffDice SuperAdmin messages — Users, Roles and permissions, Reference data, Feature flags, Plans and Account.
 *
 * Every key starts `plat.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const PLATFORM_MESSAGES = {
  /* Account */
  'plat.account.title': { en: 'Account', es: 'Cuenta', 'pt-BR': 'Conta' },
  'plat.account.changePassword': { en: 'Change password', es: 'Cambiar contraseña', 'pt-BR': 'Alterar senha' },

  /* Plans — on hold screen */
  'plat.plans.what': {
    en: 'Subscription plans, and which operator is on which, will be managed here.',
    es: 'Aquí se gestionarán los planes de suscripción y qué operador está en cada uno.',
    'pt-BR': 'Aqui serão gerenciados os planos de assinatura e qual operador está em cada um.',
  },
  'plat.plans.meanwhile': {
    en: "Plans are on hold until the Enterprise feature set is decided. No operator's access depends on a plan today.",
    es: 'Los planes están en pausa hasta que se defina el conjunto de funciones Enterprise. Hoy el acceso de ningún operador depende de un plan.',
    'pt-BR': 'Os planos estão em espera até que o conjunto de recursos Enterprise seja definido. Hoje o acesso de nenhum operador depende de um plano.',
  },

  /* Roles & permissions (Rule Book 3.12) */
  'plat.roles.intro': {
    en: 'What each platform role may do. A change applies from each person’s next request.',
    es: 'Lo que puede hacer cada rol de la plataforma. Un cambio se aplica desde la siguiente solicitud de cada persona.',
    'pt-BR': 'O que cada função da plataforma pode fazer. Uma alteração vale a partir da próxima solicitação de cada pessoa.',
  },
  'plat.roles.readOnly': {
    en: 'You can see who may do what. Changing it needs the permission to manage platform roles and permissions.',
    es: 'Puede ver quién puede hacer qué. Para cambiarlo necesita el permiso para gestionar roles y permisos de la plataforma.',
    'pt-BR': 'Você pode ver quem pode fazer o quê. Para alterar, é preciso a permissão para gerenciar funções e permissões da plataforma.',
  },
  'plat.roles.forbidden': {
    en: 'Your account cannot open roles and permissions.',
    es: 'Su cuenta no puede abrir roles y permisos.',
    'pt-BR': 'Sua conta não pode abrir funções e permissões.',
  },
  'plat.roles.ownRole': {
    en: 'You hold this role, so another person has to change it.',
    es: 'Usted tiene este rol, así que otra persona debe cambiarlo.',
    'pt-BR': 'Você tem esta função, então outra pessoa precisa alterá-la.',
  },
  'plat.roles.superAdminNote': {
    en: 'The super admin is not a column here: it always holds every permission, and nothing on this page can change that.',
    es: 'El superadministrador no aparece como columna: siempre tiene todos los permisos y nada en esta página puede cambiarlo.',
    'pt-BR': 'O superadministrador não aparece como coluna: ele sempre tem todas as permissões, e nada nesta página pode mudar isso.',
  },
  'plat.roles.ownRoleChip': { en: 'Your role', es: 'Su rol', 'pt-BR': 'Sua função' },
  'plat.roles.col.permission': { en: 'Permission', es: 'Permiso', 'pt-BR': 'Permissão' },
  'plat.roles.panel.matrix': { en: 'Roles', es: 'Roles', 'pt-BR': 'Funções' },
  'plat.roles.panel.users': { en: 'Extra permissions for one person', es: 'Permisos adicionales para una persona', 'pt-BR': 'Permissões extras para uma pessoa' },
  'plat.roles.panel.history': { en: 'Changes', es: 'Cambios', 'pt-BR': 'Alterações' },
  'plat.roles.unsaved.one': { en: '{count} unsaved change', es: '{count} cambio sin guardar', 'pt-BR': '{count} alteração não salva' },
  'plat.roles.unsaved.other': { en: '{count} unsaved changes', es: '{count} cambios sin guardar', 'pt-BR': '{count} alterações não salvas' },
  'plat.roles.action.save': { en: 'Save changes…', es: 'Guardar cambios…', 'pt-BR': 'Salvar alterações…' },
  'plat.roles.action.discard': { en: 'Discard', es: 'Descartar', 'pt-BR': 'Descartar' },
  'plat.roles.action.change': { en: 'Change…', es: 'Cambiar…', 'pt-BR': 'Alterar…' },
  'plat.roles.action.confirm': { en: 'Save', es: 'Guardar', 'pt-BR': 'Salvar' },
  'plat.roles.action.working': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },
  'plat.roles.confirm.title': { en: 'Save permission changes', es: 'Guardar cambios de permisos', 'pt-BR': 'Salvar alterações de permissões' },
  'plat.roles.confirm.userTitle': { en: 'Extra permissions for {name}', es: 'Permisos adicionales para {name}', 'pt-BR': 'Permissões extras para {name}' },
  'plat.roles.confirm.reason': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'plat.roles.confirm.reasonHint': {
    en: 'Kept with the change in the audit record.',
    es: 'Se guarda con el cambio en el registro de auditoría.',
    'pt-BR': 'Fica guardado com a alteração no registro de auditoria.',
  },
  'plat.roles.confirm.reasonMissing': { en: 'Say why before saving.', es: 'Indique el motivo antes de guardar.', 'pt-BR': 'Informe o motivo antes de salvar.' },
  'plat.roles.saved': {
    en: 'Saved. It applies from each person’s next request.',
    es: 'Guardado. Se aplica desde la siguiente solicitud de cada persona.',
    'pt-BR': 'Salvo. Vale a partir da próxima solicitação de cada pessoa.',
  },
  'plat.roles.grant': { en: 'Gives', es: 'Concede', 'pt-BR': 'Concede' },
  'plat.roles.revoke': { en: 'Takes away', es: 'Retira', 'pt-BR': 'Retira' },
  'plat.roles.nothingChanged': { en: 'No change', es: 'Sin cambios', 'pt-BR': 'Sem alteração' },
  'plat.roles.fromRole': { en: 'From their role', es: 'Por su rol', 'pt-BR': 'Pela função' },
  'plat.roles.ownAccount': {
    en: 'This is your account, so another person has to change it.',
    es: 'Esta es su cuenta, así que otra persona debe cambiarla.',
    'pt-BR': 'Esta é a sua conta, então outra pessoa precisa alterá-la.',
  },
  'plat.roles.users.empty': {
    en: 'No platform users besides the super admin.',
    es: 'No hay usuarios de la plataforma aparte del superadministrador.',
    'pt-BR': 'Nenhum usuário da plataforma além do superadministrador.',
  },
  'plat.roles.users.col.person': { en: 'Person', es: 'Persona', 'pt-BR': 'Pessoa' },
  'plat.roles.users.col.role': { en: 'Role', es: 'Rol', 'pt-BR': 'Função' },
  'plat.roles.users.col.extra': { en: 'Extra permissions', es: 'Permisos adicionales', 'pt-BR': 'Permissões extras' },
  'plat.roles.users.none': { en: 'None', es: 'Ninguno', 'pt-BR': 'Nenhuma' },
  'plat.roles.history.empty': {
    en: 'Nobody has changed a permission yet.',
    es: 'Nadie ha cambiado un permiso todavía.',
    'pt-BR': 'Ninguém alterou uma permissão ainda.',
  },
  'plat.roles.history.col.when': { en: 'When', es: 'Cuándo', 'pt-BR': 'Quando' },
  'plat.roles.history.col.who': { en: 'Who', es: 'Quién', 'pt-BR': 'Quem' },
  'plat.roles.history.col.what': { en: 'Change', es: 'Cambio', 'pt-BR': 'Alteração' },
  'plat.roles.history.col.why': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'plat.roles.history.system': { en: 'System', es: 'Sistema', 'pt-BR': 'Sistema' },
  'plat.roles.group.view': { en: 'View', es: 'Ver', 'pt-BR': 'Ver' },
  'plat.roles.group.change': { en: 'Change', es: 'Cambiar', 'pt-BR': 'Alterar' },
  'plat.roles.group.accounts': { en: 'Accounts', es: 'Cuentas', 'pt-BR': 'Contas' },
  'plat.roles.group.loginAs': { en: 'Log in as', es: 'Iniciar sesión como', 'pt-BR': 'Entrar como' },
  'plat.roles.group.platform': { en: 'Platform', es: 'Plataforma', 'pt-BR': 'Plataforma' },
  'plat.roles.cap.operators.view': { en: 'View operators', es: 'Ver operadores', 'pt-BR': 'Ver operadores' },
  'plat.roles.cap.brands.view': { en: 'View brands', es: 'Ver marcas', 'pt-BR': 'Ver marcas' },
  'plat.roles.cap.affiliates.view': { en: 'View affiliates', es: 'Ver afiliados', 'pt-BR': 'Ver afiliados' },
  'plat.roles.cap.entities.edit': { en: 'Add and edit', es: 'Añadir y editar', 'pt-BR': 'Adicionar e editar' },
  'plat.roles.cap.entities.suspend': { en: 'Approve, suspend and enable', es: 'Aprobar, suspender y habilitar', 'pt-BR': 'Aprovar, suspender e habilitar' },
  'plat.roles.cap.entities.delete': { en: 'Delete (can be undone)', es: 'Eliminar (se puede deshacer)', 'pt-BR': 'Excluir (pode ser desfeito)' },
  'plat.roles.cap.entities.restore': { en: 'Restore what was deleted', es: 'Restaurar lo eliminado', 'pt-BR': 'Restaurar o que foi excluído' },
  'plat.roles.cap.entities.purge': { en: 'Remove permanently', es: 'Eliminar definitivamente', 'pt-BR': 'Remover definitivamente' },
  'plat.roles.cap.operator_team.manage': { en: 'Manage an operator’s team', es: 'Gestionar el equipo de un operador', 'pt-BR': 'Gerenciar a equipe de um operador' },
  'plat.roles.cap.credentials.reset': { en: 'Reset two-factor sign-in and unlock', es: 'Restablecer el segundo factor y desbloquear', 'pt-BR': 'Redefinir o segundo fator e desbloquear' },
  'plat.roles.cap.login_as.operator_user': { en: 'An operator user', es: 'Un usuario de operador', 'pt-BR': 'Um usuário de operador' },
  'plat.roles.cap.login_as.brand_user': { en: 'A brand user', es: 'Un usuario de marca', 'pt-BR': 'Um usuário de marca' },
  'plat.roles.cap.login_as.affiliate': { en: 'An affiliate', es: 'Un afiliado', 'pt-BR': 'Um afiliado' },
  'plat.roles.cap.platform_users.manage': { en: 'Manage platform users', es: 'Gestionar usuarios de la plataforma', 'pt-BR': 'Gerenciar usuários da plataforma' },
  'plat.roles.cap.platform_permissions.manage': { en: 'Manage roles and permissions', es: 'Gestionar roles y permisos', 'pt-BR': 'Gerenciar funções e permissões' },
  'plat.roles.cap.audit.view': {
    en: 'View the audit records and access log',
    es: 'Ver los registros de auditoría y el registro de accesos',
    'pt-BR': 'Ver os registros de auditoria e o registro de acessos',
  },
  'plat.roles.cap.platform.configure': {
    en: 'Change platform settings',
    es: 'Cambiar la configuración de la plataforma',
    'pt-BR': 'Alterar as configurações da plataforma',
  },

  /* Feature flags */
  'plat.flags.panel': { en: 'Flags', es: 'Funciones', 'pt-BR': 'Recursos' },
  'plat.flags.sections': { en: 'Flag sections', es: 'Secciones de funciones', 'pt-BR': 'Seções de recursos' },
  'plat.flags.allSections': { en: 'All flags ({n})', es: 'Todas las funciones ({n})', 'pt-BR': 'Todos os recursos ({n})' },
  'plat.flags.otherSection': { en: 'Other', es: 'Otras', 'pt-BR': 'Outros' },
  'plat.flags.empty': { en: 'No flags yet.', es: 'Aún no hay funciones.', 'pt-BR': 'Ainda não há recursos.' },
  'plat.flags.colFlag': { en: 'Flag', es: 'Función', 'pt-BR': 'Recurso' },
  'plat.flags.colDefault': { en: 'Default', es: 'Predeterminado', 'pt-BR': 'Padrão' },
  'plat.flags.colOverrides': { en: 'Overrides', es: 'Excepciones', 'pt-BR': 'Exceções' },
  'plat.flags.colSetting': { en: 'Setting', es: 'Ajuste', 'pt-BR': 'Configuração' },
  'plat.flags.on': { en: 'On', es: 'Activada', 'pt-BR': 'Ativado' },
  'plat.flags.off': { en: 'Off', es: 'Desactivada', 'pt-BR': 'Desativado' },
  'plat.flags.turnOffAll': {
    en: 'Turn off for every operator…',
    es: 'Desactivar para todos los operadores…',
    'pt-BR': 'Desativar para todos os operadores…',
  },
  'plat.flags.turnOnAll': {
    en: 'Turn on for every operator…',
    es: 'Activar para todos los operadores…',
    'pt-BR': 'Ativar para todos os operadores…',
  },
  'plat.flags.readOnlyOn': {
    en: 'On for every operator without an override.',
    es: 'Activada para todos los operadores sin excepción propia.',
    'pt-BR': 'Ativado para todos os operadores sem exceção própria.',
  },
  'plat.flags.readOnlyOff': {
    en: 'Off for every operator.',
    es: 'Desactivada para todos los operadores.',
    'pt-BR': 'Desativado para todos os operadores.',
  },
  'plat.flags.turnOffTitle': {
    en: 'Turn off {flag} for every operator?',
    es: '¿Desactivar {flag} para todos los operadores?',
    'pt-BR': 'Desativar {flag} para todos os operadores?',
  },
  'plat.flags.turnOffConfirm': {
    en: 'Turn off for every operator',
    es: 'Desactivar para todos los operadores',
    'pt-BR': 'Desativar para todos os operadores',
  },
  'plat.flags.offNobodyOn': {
    en: 'No operator is on right now, so this only changes the default new operators get.',
    es: 'Ningún operador la tiene activada ahora, así que esto solo cambia el valor predeterminado de los operadores nuevos.',
    'pt-BR': 'Nenhum operador está com o recurso ativado agora, então isso só altera o padrão dos novos operadores.',
  },
  'plat.flags.offNobodyOnDeletes.one': {
    en: 'It still deletes the {count} existing override for this flag.',
    es: 'Aun así elimina la {count} excepción existente de esta función.',
    'pt-BR': 'Ainda assim, exclui a {count} exceção existente deste recurso.',
  },
  'plat.flags.offNobodyOnDeletes.other': {
    en: 'It still deletes all {count} existing overrides for this flag.',
    es: 'Aun así elimina las {count} excepciones existentes de esta función.',
    'pt-BR': 'Ainda assim, exclui as {count} exceções existentes deste recurso.',
  },
  'plat.flags.offEveryone.one': {
    en: 'Turns it off for every operator, with no exceptions — including {names}, who is on right now.',
    es: 'La desactiva para todos los operadores, sin excepciones, incluido {names}, que la tiene activada ahora.',
    'pt-BR': 'Desativa para todos os operadores, sem exceções, incluindo {names}, que está com o recurso ativado agora.',
  },
  'plat.flags.offEveryone.other': {
    en: 'Turns it off for every operator, with no exceptions — including {names}, who are on right now.',
    es: 'La desactiva para todos los operadores, sin excepciones, incluidos {names}, que la tienen activada ahora.',
    'pt-BR': 'Desativa para todos os operadores, sem exceções, incluindo {names}, que estão com o recurso ativado agora.',
  },
  'plat.flags.offEveryoneDeletes.one': {
    en: 'It deletes the {count} existing override for this flag — an override that was keeping an operator on is cleared the same as one that was keeping them off.',
    es: 'Elimina la {count} excepción existente de esta función: una excepción que mantenía activada a un operador se borra igual que una que la mantenía desactivada.',
    'pt-BR': 'Exclui a {count} exceção existente deste recurso — uma exceção que mantinha um operador ativado é removida da mesma forma que uma que o mantinha desativado.',
  },
  'plat.flags.offEveryoneDeletes.other': {
    en: 'It deletes all {count} existing overrides for this flag — an override that was keeping an operator on is cleared the same as one that was keeping them off.',
    es: 'Elimina las {count} excepciones existentes de esta función: una excepción que mantenía activado a un operador se borra igual que una que lo mantenía desactivado.',
    'pt-BR': 'Exclui as {count} exceções existentes deste recurso — uma exceção que mantinha um operador ativado é removida da mesma forma que uma que o mantinha desativado.',
  },
  'plat.flags.pickTitle': {
    en: 'Turn {flag} on — for whom?',
    es: 'Activar {flag}: ¿para quién?',
    'pt-BR': 'Ativar {flag}: para quem?',
  },
  'plat.flags.pickNote': {
    en: "This turns the platform default on, but every operator that exists today is written its own setting the moment you press the button — on for who you pick below, off for everyone else, even one that already held an override either way. Pick nobody and it changes nothing for today's operators; only one created after this reaches it, from the new default.",
    es: 'Esto activa el valor predeterminado de la plataforma, pero en el momento en que pulses el botón cada operador existente recibe su propio ajuste: activada para quienes elijas abajo y desactivada para todos los demás, incluso para quien ya tuviera una excepción en cualquier sentido. Si no eliges a nadie, no cambia nada para los operadores actuales; solo la recibe uno creado después, a partir del nuevo valor predeterminado.',
    'pt-BR': 'Isto ativa o padrão da plataforma, mas no momento em que você clicar no botão cada operador existente recebe sua própria configuração: ativado para quem você escolher abaixo e desativado para todos os demais, mesmo para quem já tinha uma exceção em qualquer sentido. Se você não escolher ninguém, nada muda para os operadores atuais; só quem for criado depois recebe o recurso, a partir do novo padrão.',
  },
  'plat.flags.turnOnNewOnly': {
    en: 'Turn on for new operators only',
    es: 'Activar solo para operadores nuevos',
    'pt-BR': 'Ativar apenas para novos operadores',
  },
  'plat.flags.turnOnFor.one': {
    en: 'Turn on for {count} operator',
    es: 'Activar para {count} operador',
    'pt-BR': 'Ativar para {count} operador',
  },
  'plat.flags.turnOnFor.other': {
    en: 'Turn on for {count} operators',
    es: 'Activar para {count} operadores',
    'pt-BR': 'Ativar para {count} operadores',
  },
  'plat.flags.noOverrides': {
    en: 'No per-operator overrides — every operator follows the default above.',
    es: 'No hay excepciones por operador: todos los operadores siguen el valor predeterminado de arriba.',
    'pt-BR': 'Não há exceções por operador: todos os operadores seguem o padrão acima.',
  },
  'plat.flags.clearLink': {
    en: 'Clear override (follow default)',
    es: 'Quitar excepción (seguir el predeterminado)',
    'pt-BR': 'Remover exceção (seguir o padrão)',
  },
  'plat.flags.clearTitle': {
    en: "Clear {operator}'s override?",
    es: '¿Quitar la excepción de {operator}?',
    'pt-BR': 'Remover a exceção de {operator}?',
  },
  'plat.flags.clearConfirm': { en: 'Clear override', es: 'Quitar excepción', 'pt-BR': 'Remover exceção' },
  'plat.flags.clearBodyBefore': {
    en: '{operator} stops holding its own setting for {flag} and follows the default instead — currently ',
    es: '{operator} deja de tener su propio ajuste para {flag} y sigue el valor predeterminado, que ahora es ',
    'pt-BR': '{operator} deixa de ter sua própria configuração para {flag} e passa a seguir o padrão, que atualmente é ',
  },
  'plat.flags.clearBodyOn': { en: 'on', es: 'activada', 'pt-BR': 'ativado' },
  'plat.flags.clearBodyOff': { en: 'off', es: 'desactivada', 'pt-BR': 'desativado' },
  'plat.flags.clearBodyAfter': {
    en: ' for every operator without an override.',
    es: ' para todos los operadores sin excepción.',
    'pt-BR': ' para todos os operadores sem exceção.',
  },
  'plat.flags.exemptLabel': {
    en: 'Exempt one operator from the default',
    es: 'Exceptuar a un operador del valor predeterminado',
    'pt-BR': 'Abrir exceção para um operador em relação ao padrão',
  },
  'plat.flags.chooseOperator': { en: 'Choose an operator…', es: 'Elige un operador…', 'pt-BR': 'Escolha um operador…' },
  'plat.flags.settingFor': {
    en: 'Setting for that operator',
    es: 'Ajuste para ese operador',
    'pt-BR': 'Configuração para esse operador',
  },
  'plat.flags.setOverride': { en: 'Set override', es: 'Fijar excepción', 'pt-BR': 'Definir exceção' },

  /* Reference data */
  'plat.ref.sectionsLabel': {
    en: 'Reference data types',
    es: 'Tipos de datos de referencia',
    'pt-BR': 'Tipos de dados de referência',
  },
  'plat.ref.groupCatalogue': { en: 'Catalogue', es: 'Catálogo', 'pt-BR': 'Catálogo' },
  'plat.ref.groupCommission': { en: 'Commission fields', es: 'Campos de comisión', 'pt-BR': 'Campos de comissão' },
  'plat.ref.code': { en: 'Code', es: 'Código', 'pt-BR': 'Código' },
  'plat.ref.codeJurisdiction': { en: 'Code (e.g. US-NJ)', es: 'Código (p. ej., US-NJ)', 'pt-BR': 'Código (ex.: US-NJ)' },
  'plat.ref.codeCurrency': { en: 'Code (ISO 4217)', es: 'Código (ISO 4217)', 'pt-BR': 'Código (ISO 4217)' },
  'plat.ref.type.products': { en: 'Products', es: 'Productos', 'pt-BR': 'Produtos' },
  'plat.ref.type.jurisdictions': { en: 'Jurisdictions', es: 'Jurisdicciones', 'pt-BR': 'Jurisdições' },
  'plat.ref.type.currencies': { en: 'Currencies', es: 'Monedas', 'pt-BR': 'Moedas' },
  'plat.ref.type.devices': { en: 'Devices', es: 'Dispositivos', 'pt-BR': 'Dispositivos' },
  'plat.ref.type.os': { en: 'Operating systems', es: 'Sistemas operativos', 'pt-BR': 'Sistemas operacionais' },
  'plat.ref.type.commissionTypes': { en: 'Commission types', es: 'Tipos de comisión', 'pt-BR': 'Tipos de comissão' },
  'plat.ref.type.revenueBases': { en: 'Revenue bases', es: 'Bases de ingresos', 'pt-BR': 'Bases de receita' },
  'plat.ref.type.tierMetrics': { en: 'Tier measures', es: 'Medidas de nivel', 'pt-BR': 'Medidas de nível' },
  'plat.ref.type.hybridModes': { en: 'Hybrid interactions', es: 'Interacciones híbridas', 'pt-BR': 'Interações híbridas' },
  'plat.ref.type.cpaTriggers': { en: 'Commission variables', es: 'Variables de comisión', 'pt-BR': 'Variáveis de comissão' },
  'plat.ref.type.deductionItems': { en: 'Deduction items', es: 'Conceptos de deducción', 'pt-BR': 'Itens de dedução' },
  'plat.ref.type.cpaCriteria': { en: 'Qualification criteria', es: 'Criterios de calificación', 'pt-BR': 'Critérios de qualificação' },
  'plat.ref.single.products': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'plat.ref.single.jurisdictions': { en: 'Jurisdiction', es: 'Jurisdicción', 'pt-BR': 'Jurisdição' },
  'plat.ref.single.currencies': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'plat.ref.single.devices': { en: 'Device', es: 'Dispositivo', 'pt-BR': 'Dispositivo' },
  'plat.ref.single.os': { en: 'Operating system', es: 'Sistema operativo', 'pt-BR': 'Sistema operacional' },
  'plat.ref.single.commissionTypes': { en: 'Commission type', es: 'Tipo de comisión', 'pt-BR': 'Tipo de comissão' },
  'plat.ref.single.revenueBases': { en: 'Revenue basis', es: 'Base de ingresos', 'pt-BR': 'Base de receita' },
  'plat.ref.single.tierMetrics': { en: 'Tier measure', es: 'Medida de nivel', 'pt-BR': 'Medida de nível' },
  'plat.ref.single.hybridModes': { en: 'Hybrid interaction', es: 'Interacción híbrida', 'pt-BR': 'Interação híbrida' },
  'plat.ref.single.cpaTriggers': { en: 'Commission variable', es: 'Variable de comisión', 'pt-BR': 'Variável de comissão' },
  'plat.ref.single.deductionItems': { en: 'Deduction item', es: 'Concepto de deducción', 'pt-BR': 'Item de dedução' },
  'plat.ref.single.cpaCriteria': { en: 'Qualification criterion', es: 'Criterio de calificación', 'pt-BR': 'Critério de qualificação' },
  'plat.ref.field.feeBased': {
    en: 'Fee-based (operator takes a fee, carries no position)',
    es: 'Basado en comisión (el operador cobra una tarifa y no asume posición)',
    'pt-BR': 'Baseado em taxa (o operador cobra uma taxa e não assume posição)',
  },
  'plat.ref.field.country': {
    en: 'Country (ISO 3166-1 alpha-2)',
    es: 'País (ISO 3166-1 alfa-2)',
    'pt-BR': 'País (ISO 3166-1 alfa-2)',
  },
  'plat.ref.field.subdivision': {
    en: 'Subdivision (blank for a country row)',
    es: 'Subdivisión (vacía para una fila de país)',
    'pt-BR': 'Subdivisão (vazia para uma linha de país)',
  },
  'plat.ref.field.regulatesBySubdivision': {
    en: 'Regulates by subdivision',
    es: 'Regula por subdivisión',
    'pt-BR': 'Regula por subdivisão',
  },
  'plat.ref.field.minorUnitScale': {
    en: 'Minor unit scale (2 for USD, 0 for JPY, 8 for BTC)',
    es: 'Escala de la unidad menor (2 para USD, 0 para JPY, 8 para BTC)',
    'pt-BR': 'Escala da unidade menor (2 para USD, 0 para JPY, 8 para BTC)',
  },
  'plat.ref.field.crypto': { en: 'Crypto', es: 'Cripto', 'pt-BR': 'Cripto' },
  'plat.ref.field.label': { en: 'Label', es: 'Etiqueta', 'pt-BR': 'Rótulo' },
  'plat.ref.field.detectPattern': {
    en: 'Detect pattern (matched against the user-agent, e.g. harmonyos)',
    es: 'Patrón de detección (se compara con el user-agent, p. ej., harmonyos)',
    'pt-BR': 'Padrão de detecção (comparado com o user-agent, ex.: harmonyos)',
  },
  'plat.ref.field.matchOrder': {
    en: 'Match order (lower is tried first — an iPhone user-agent also contains "Mac OS X")',
    es: 'Orden de coincidencia (el menor se prueba primero; el user-agent de un iPhone también contiene "Mac OS X")',
    'pt-BR': 'Ordem de correspondência (o menor é testado primeiro; o user-agent de um iPhone também contém "Mac OS X")',
  },
  'plat.ref.field.orderDefault': {
    en: 'Order (lowest is offered first, and is the default)',
    es: 'Orden (el menor se ofrece primero y es el predeterminado)',
    'pt-BR': 'Ordem (o menor é oferecido primeiro e é o padrão)',
  },
  'plat.ref.field.order': {
    en: 'Order (lowest is offered first)',
    es: 'Orden (el menor se ofrece primero)',
    'pt-BR': 'Ordem (o menor é oferecido primeiro)',
  },
  'plat.ref.curatedWarnNone': {
    en: 'No operator will be offered {code} again on a campaign payout or a deal proposal, and a proposal naming it is refused. No deal is currently paying under it, so nothing stops. Reactivating it puts everything back, including the periods that were skipped while it was off, once those periods are run again.',
    es: 'A ningún operador se le volverá a ofrecer {code} en el pago de una campaña ni en una propuesta de acuerdo, y se rechaza una propuesta que lo nombre. Ningún acuerdo paga actualmente con este valor, así que no se detiene nada. Al reactivarlo, todo vuelve a su sitio, incluidos los períodos que se omitieron mientras estaba desactivado, una vez que esos períodos se ejecuten de nuevo.',
    'pt-BR': 'Nenhum operador voltará a ver {code} como opção no pagamento de uma campanha ou em uma proposta de acordo, e uma proposta que o cite é recusada. Nenhum acordo paga atualmente com ele, então nada é interrompido. Reativá-lo restaura tudo, inclusive os períodos que foram ignorados enquanto estava desativado, depois que esses períodos forem executados novamente.',
  },
  'plat.ref.curatedWarn.one': {
    en: 'No operator will be offered {code} again on a campaign payout or a deal proposal, and a proposal naming it is refused. {count} live deal is paying under it today. It will be paid in full for the period now under way, and from the next period it computes nothing — no commission line at all, with the reason recorded against the run. Nobody is paid a zero; nobody is paid anything either. Reactivating it puts everything back, including the periods that were skipped while it was off, once those periods are run again.',
    es: 'A ningún operador se le volverá a ofrecer {code} en el pago de una campaña ni en una propuesta de acuerdo, y se rechaza una propuesta que lo nombre. {count} acuerdo activo paga con este valor hoy. Se pagará íntegro por el período en curso y, desde el siguiente período, no calcula nada: ninguna línea de comisión, con el motivo registrado en la ejecución. No se paga un cero; tampoco se paga nada. Al reactivarlo, todo vuelve a su sitio, incluidos los períodos que se omitieron mientras estaba desactivado, una vez que esos períodos se ejecuten de nuevo.',
    'pt-BR': 'Nenhum operador voltará a ver {code} como opção no pagamento de uma campanha ou em uma proposta de acordo, e uma proposta que o cite é recusada. {count} acordo ativo paga com ele hoje. Ele será pago integralmente pelo período em andamento e, a partir do próximo período, não calcula nada: nenhuma linha de comissão, com o motivo registrado na execução. Ninguém recebe um zero; ninguém recebe nada. Reativá-lo restaura tudo, inclusive os períodos que foram ignorados enquanto estava desativado, depois que esses períodos forem executados novamente.',
  },
  'plat.ref.curatedWarn.other': {
    en: 'No operator will be offered {code} again on a campaign payout or a deal proposal, and a proposal naming it is refused. {count} live deals are paying under it today. They will be paid in full for the period now under way, and from the next period they compute nothing — no commission line at all, with the reason recorded against the run. Nobody is paid a zero; nobody is paid anything either. Reactivating it puts everything back, including the periods that were skipped while it was off, once those periods are run again.',
    es: 'A ningún operador se le volverá a ofrecer {code} en el pago de una campaña ni en una propuesta de acuerdo, y se rechaza una propuesta que lo nombre. {count} acuerdos activos pagan con este valor hoy. Se pagarán íntegros por el período en curso y, desde el siguiente período, no calculan nada: ninguna línea de comisión, con el motivo registrado en la ejecución. No se paga un cero; tampoco se paga nada. Al reactivarlo, todo vuelve a su sitio, incluidos los períodos que se omitieron mientras estaba desactivado, una vez que esos períodos se ejecuten de nuevo.',
    'pt-BR': 'Nenhum operador voltará a ver {code} como opção no pagamento de uma campanha ou em uma proposta de acordo, e uma proposta que o cite é recusada. {count} acordos ativos pagam com ele hoje. Eles serão pagos integralmente pelo período em andamento e, a partir do próximo período, não calculam nada: nenhuma linha de comissão, com o motivo registrado na execução. Ninguém recebe um zero; ninguém recebe nada. Reativá-lo restaura tudo, inclusive os períodos que foram ignorados enquanto estava desativado, depois que esses períodos forem executados novamente.',
  },
  'plat.ref.toggle.busyActive': {
    en: '{code} is still active. The change has not been applied yet.',
    es: '{code} sigue activo. El cambio aún no se ha aplicado.',
    'pt-BR': '{code} continua ativo. A alteração ainda não foi aplicada.',
  },
  'plat.ref.toggle.busyInactive': {
    en: '{code} is still inactive. The change has not been applied yet.',
    es: '{code} sigue inactivo. El cambio aún no se ha aplicado.',
    'pt-BR': '{code} continua inativo. A alteração ainda não foi aplicada.',
  },
  'plat.ref.toggle.active': {
    en: '{code} is active. Deactivate it.',
    es: '{code} está activo. Desactívalo.',
    'pt-BR': '{code} está ativo. Desative-o.',
  },
  'plat.ref.toggle.inactive': {
    en: '{code} is inactive. Activate it.',
    es: '{code} está inactivo. Actívalo.',
    'pt-BR': '{code} está inativo. Ative-o.',
  },
  'plat.ref.search': { en: 'Search by code or name', es: 'Buscar por código o nombre', 'pt-BR': 'Buscar por código ou nome' },
  'plat.ref.importCsv': { en: 'Import CSV', es: 'Importar CSV', 'pt-BR': 'Importar CSV' },
  'plat.ref.add': { en: 'Add {item}', es: 'Añadir {item}', 'pt-BR': 'Adicionar {item}' },
  'plat.ref.nothingYet': { en: 'Nothing yet.', es: 'Todavía no hay nada.', 'pt-BR': 'Ainda não há nada.' },
  'plat.ref.noMatch': {
    en: 'Nothing in {type} matches “{query}”. Clear the search to see all {count} again.',
    es: 'Nada en {type} coincide con “{query}”. Borra la búsqueda para volver a ver los {count}.',
    'pt-BR': 'Nada em {type} corresponde a “{query}”. Limpe a busca para ver todos os {count} novamente.',
  },
  'plat.ref.colLiveDeals': { en: 'Live deals', es: 'Acuerdos activos', 'pt-BR': 'Acordos ativos' },
  'plat.ref.editCode': { en: 'Edit {code}', es: 'Editar {code}', 'pt-BR': 'Editar {code}' },
  'plat.ref.deleteCode': { en: 'Delete {code}', es: 'Eliminar {code}', 'pt-BR': 'Excluir {code}' },
  'plat.ref.codeHint': {
    en: 'Derived from Country and Subdivision below.',
    es: 'Se deriva de País y Subdivisión, más abajo.',
    'pt-BR': 'Derivado de País e Subdivisão, abaixo.',
  },
  'plat.ref.create': { en: 'Create', es: 'Crear', 'pt-BR': 'Criar' },
  'plat.ref.deactivateTitle': { en: 'Deactivate {code}?', es: '¿Desactivar {code}?', 'pt-BR': 'Desativar {code}?' },
  'plat.ref.deactivate': { en: 'Deactivate', es: 'Desactivar', 'pt-BR': 'Desativar' },
  'plat.ref.deactivateJurisdiction': {
    en: 'Every campaign targeting {code}, on every operator licensed there, pauses along with it — and every tracking link on those campaigns stops attributing. Reactivating brings back exactly what this deactivation itself paused, not anything an operator paused on their own.',
    es: 'Todas las campañas dirigidas a {code}, en todos los operadores con licencia allí, se pausan con ella, y todos los enlaces de seguimiento de esas campañas dejan de atribuir. Al reactivarla se recupera exactamente lo que esta desactivación pausó, no lo que un operador haya pausado por su cuenta.',
    'pt-BR': 'Todas as campanhas direcionadas a {code}, em todos os operadores licenciados lá, são pausadas junto com ela, e todos os links de rastreamento dessas campanhas deixam de atribuir. Reativá-la restaura exatamente o que esta desativação pausou, não o que um operador pausou por conta própria.',
  },
  'plat.ref.deactivateDefault': {
    en: '{code} can no longer be newly declared by an operator. Anything already using it is untouched.',
    es: 'Un operador ya no puede declarar {code} de nuevo. Lo que ya lo use no se toca.',
    'pt-BR': 'Um operador não pode mais declarar {code} novamente. O que já o usa permanece intacto.',
  },
  'plat.ref.deleteTitle': { en: 'Delete {code}?', es: '¿Eliminar {code}?', 'pt-BR': 'Excluir {code}?' },
  'plat.ref.deleteBody': {
    en: 'This only works if nothing uses it yet — otherwise the delete is refused, not forced.',
    es: 'Solo funciona si todavía nada lo usa; de lo contrario, la eliminación se rechaza, no se fuerza.',
    'pt-BR': 'Só funciona se nada o usar ainda; caso contrário, a exclusão é recusada, não forçada.',
  },
  'plat.ref.importTitle': {
    en: 'Import {type} from CSV',
    es: 'Importar {type} desde CSV',
    'pt-BR': 'Importar {type} de CSV',
  },
  'plat.ref.importIntro': {
    en: 'The first row names the columns, and these are the ones this accepts: ',
    es: 'La primera fila nombra las columnas, y estas son las que se aceptan: ',
    'pt-BR': 'A primeira linha nomeia as colunas, e estas são as aceitas: ',
  },
  'plat.ref.importOutro': {
    en: '. A code that already exists is updated; one that does not is added. A blank cell leaves that column as it is. Nothing is applied unless every row reads correctly, so a file that is refused can be corrected and sent again.',
    es: '. Un código que ya existe se actualiza; uno que no existe se añade. Una celda vacía deja esa columna como está. No se aplica nada a menos que todas las filas se lean correctamente, así que un archivo rechazado se puede corregir y enviar de nuevo.',
    'pt-BR': '. Um código que já existe é atualizado; um que não existe é adicionado. Uma célula vazia deixa essa coluna como está. Nada é aplicado a menos que todas as linhas sejam lidas corretamente, então um arquivo recusado pode ser corrigido e enviado novamente.',
  },
  'plat.ref.file': { en: 'File', es: 'Archivo', 'pt-BR': 'Arquivo' },
  'plat.ref.theFile': { en: 'the file', es: 'el archivo', 'pt-BR': 'o arquivo' },
  'plat.ref.importNoneApplied': {
    en: 'None of {file} was applied. These lines could not be read:',
    es: 'No se aplicó nada de {file}. Estas líneas no se pudieron leer:',
    'pt-BR': 'Nada de {file} foi aplicado. Estas linhas não puderam ser lidas:',
  },
  'plat.ref.colLine': { en: 'Line', es: 'Línea', 'pt-BR': 'Linha' },
  'plat.ref.colProblem': { en: 'Problem', es: 'Problema', 'pt-BR': 'Problema' },
  'plat.ref.import': { en: 'Import', es: 'Importar', 'pt-BR': 'Importar' },
  'plat.ref.importApplied': {
    en: '{file} applied: {message}',
    es: '{file} aplicado: {message}',
    'pt-BR': '{file} aplicado: {message}',
  },
} as const satisfies Catalogue;
